// @ts-nocheck
/*
 * Port of views/dashboard/voice-plan/add.js (origin/main-dev, 446 lines).
 * BYTE-IDENTICAL — demoExists prefetch (only shows Demo checkbox when
 * superadmin AND no demo exists yet); getAllowedVoicePlanDirections
 * derives allowedPlanTypes from ACL; canUseVoicePlanType per-plan gate;
 * groupPlansForStorage payload shape. Chrome swap: v1 <Card> → shadcn.
 */
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { postApiCall, getApiCall } from '@/service/httpCall';
import { API_VOICE_PLAN_CREATE, API_VOICE_PLAN_LIST } from '@/service/url';
import { useNavigate } from 'react-router-dom';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import {
  getAllowedVoicePlanDirections,
  canUseVoicePlanType,
  parseModuleOperations,
} from '@/utilities/voicePlanAcl';
import RenderVoicePlanForm, { MAX_PULSE_PRICE_DECIMALS } from '@/features/voice-plan/VoicePlanForm';
import {
  groupPlansForStorage,
  areAllPlansComplete,
  buildAllVoicePlanLegEntries,
  getVoicePlanKey,
} from '@/utilities/voicePlanUtils';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import '@/features/voice-plan/voice-plan-form.scss';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as PageButton } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

function getVoicePlanCreateSeed() {
  const user = getUser();
  const superAdmin = user?.role === USER_ROLES.SUPERADMIN;
  const types = getAllowedVoicePlanDirections(
    user?.acl_settings?.module_operations,
    user?.role,
    superAdmin,
  );
  const entries = buildAllVoicePlanLegEntries(types);
  return { user, superAdmin, types, entries };
}

export default function VoicePlanCreate() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [planName, setPlanName] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [demoExists, setDemoExists] = useState(false);

  useEffect(() => {
    getApiCall(API_VOICE_PLAN_LIST)
      .then((resp) => {
        if (Array.isArray(resp)) setDemoExists(resp.some((p) => p.is_demo));
      })
      .catch(() => {});
  }, []);

  const [planType, setPlanType] = useState('');
  const [legType, setLegType] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [userPulseDuration, setUserPulseDuration] = useState(null);
  const [userPulsePrice, setUserPulsePrice] = useState(Infinity);
  const [createSeed] = useState(getVoicePlanCreateSeed);
  const [plansData, setPlansData] = useState(createSeed.entries);
  const [currentPlan, setCurrentPlan] = useState(
    createSeed.entries.map((plan) => getVoicePlanKey(plan)),
  );
  const [lockedRowKeys, setLockedRowKeys] = useState([]);
  const [planNameError, setPlanNameError] = useState('');
  const [allowedPlanTypes, setAllowedPlanTypes] = useState(createSeed.types);
  const [isSuperAdmin, setIsSuperAdmin] = useState(createSeed.superAdmin);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  function validateForm() {
    if (!planName) {
      toast.warning('Plan Name required');
      return false;
    }

    if (!areAllPlansComplete(plansData)) {
      toast.warning('Fill pulse duration and price for all legs to continue');
      return false;
    }

    if (plansData.some((plan) => !lockedRowKeys.includes(getVoicePlanKey(plan)))) {
      toast.warning('Click + on all legs before creating the voice plan');
      return false;
    }

    const submittablePlans = plansData;

    if (
      submittablePlans.some(
        (plan) =>
          !canUseVoicePlanType(
            parseModuleOperations(getUser()?.acl_settings?.module_operations),
            plan.plan_type,
            { isSuperAdmin },
          ),
      )
    ) {
      toast.warning('One or more plan types are not allowed for your role.');
      return false;
    }

    for (let i = 0; i < submittablePlans.length; i++) {
      const plan = submittablePlans[i];
      const row = i + 1;

      if (plan.pulse_price === '' || plan.pulse_price === null || plan.pulse_price === undefined) {
        toast.warning(`Enter pulse price for row ${row}`);
        return false;
      }

      const priceStr = String(plan.pulse_price);
      const decimalParts = priceStr.split('.');
      if (decimalParts.length > 1 && decimalParts[1].length > MAX_PULSE_PRICE_DECIMALS) {
        toast.warning(
          `Price at row ${row} must have maximum ${MAX_PULSE_PRICE_DECIMALS} decimal places`,
        );
        return false;
      }

      if (plan.plan_type !== 'MISSED') {
        if (!plan.pulse_duration) {
          toast.warning(`Select pulse duration for row ${row}`);
          return false;
        }
      }
    }

    return true;
  }

  function submitForm(e) {
    e.preventDefault();
    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    const normalizedPlans = plansData.map((plan) => ({
      ...plan,
      pulse_price:
        typeof plan.pulse_price === 'string' ? parseFloat(plan.pulse_price) : Number(plan.pulse_price),
      pulse_duration:
        typeof plan.pulse_duration === 'string'
          ? parseInt(plan.pulse_duration, 10)
          : Number(plan.pulse_duration),
    }));

    const params = {
      plan_name: planName,
      plans: groupPlansForStorage(normalizedPlans),
      is_demo: isDemo,
    };

    postApiCall(API_VOICE_PLAN_CREATE, params).then(onVoicePlanSuccess).catch(onVoicePlanError);
  }

  function onVoicePlanSuccess() {
    toast.success('Voice plan created successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.VOICE_PLAN.LIST}`);
    }, 1500);
  }

  function onVoicePlanError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    }
    setButtonDisable(false);
    toast.error(friendlyApiError(err) || 'Failed to create voice plan');
  }

  function applyAllDefaultLegs(types) {
    const entries = buildAllVoicePlanLegEntries(types);
    setPlansData(entries);
    setCurrentPlan(entries.map((plan) => getVoicePlanKey(plan)));
    setLockedRowKeys([]);
  }

  function handleReset() {
    setPlanName('');
    setPlanType('');
    setLegType('');
    applyAllDefaultLegs(allowedPlanTypes);
    setPlanNameError('');
  }

  useEffect(() => {
    const user = getUser();
    if (user?.voice_plan_id) {
      setUserPulseDuration(user.voice_plan_id.pulse_duration);
      setUserPulsePrice(user.voice_plan_id.price);
    }
    const superAdmin = user?.role === USER_ROLES.SUPERADMIN;
    setIsSuperAdmin(superAdmin);
    const moduleOps = user?.acl_settings?.module_operations;
    const types = getAllowedVoicePlanDirections(moduleOps, user?.role, superAdmin);
    setAllowedPlanTypes(types);
    applyAllDefaultLegs(types);
  }, []);

  return (
    <ScrollPage>
      <PageHeader
        title="Create Voice Plan"
        description="Define pulse duration and per-pulse pricing for voice calls."
        actions={
          <PageButton asChild variant="outline" size="sm" className="gap-1">
            <Link to={`/${ROUTES.VOICE_PLAN.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </PageButton>
        }
      />
      <Card className="p-4 voice-plan-page">
        <form onSubmit={submitForm}>
                <RenderVoicePlanForm
                  planName={planName}
                  setPlanName={setPlanName}
                  isDemo={isDemo}
                  setIsDemo={isSuperAdmin && !demoExists ? setIsDemo : undefined}
                  setUserPulsePrice={setUserPulsePrice}
                  userPulsePrice={userPulsePrice}
                  userPulseDuration={userPulseDuration}
                  setUserPulseDuration={setUserPulseDuration}
                  setPlanType={setPlanType}
                  planType={planType}
                  legType={legType}
                  setLegType={setLegType}
                  setPlansData={setPlansData}
                  plansData={plansData}
                  currentPlan={currentPlan}
                  setCurrentPlan={setCurrentPlan}
                  lockedRowKeys={lockedRowKeys}
                  setLockedRowKeys={setLockedRowKeys}
                  setPlanNameError={setPlanNameError}
                  allowedPlanTypes={allowedPlanTypes}
                  onReset={handleReset}
                  submitDisabled={buttonDisable}
                  submitLoading={buttonDisable}
          submitLabel="Create Voice Plan"
        />
        </form>
      </Card>
    </ScrollPage>
  );
}
